"use client";

import { useQuery } from "@apollo/client";
import UserAvatar from "components/User/UserAvatar";
import {
  LiquidPillGroup,
  PageEmptyStateV2,
  PageLoadingStateV2,
  UserTooltip,
  type LiquidPillGroupItem,
} from "components/ui/v2";
import { gqlMessage } from "gql";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { IMessageReaction } from "types/Message.type";
import { IUser } from "types/User.types";
import { formatUserDisplayName } from "utils/string";

interface IReactionPageProps {
  id: string;
  type: "message" | "comment";
  embedded?: boolean;
  hideHeader?: boolean;
  /** When set, the Modal shell owns loading via bubbleReveal — no body orbit loader. */
  onLoadingChange?: (loading: boolean) => void;
  onTabChange?: (
    tabItems: LiquidPillGroupItem[],
    activeTab: string,
    setActiveTab: (val: string) => void
  ) => void;
  onCountChange?: (count: number) => void;
}

const REACTION_EMOJIS = {
  like: "👍🏻",
  love: "❤️",
  wow: "😮",
  haha: "😂",
  sad: "😢",
  angry: "😡",
} as const;

type ReactionType = keyof typeof REACTION_EMOJIS;

function ReactionUserRowV2({
  reaction,
  emoji,
}: {
  reaction: IMessageReaction;
  emoji: string;
}) {
  const { user: storeUser } = useUserStoreContext((store) => ({
    user: store.user,
  }));
  const user = reaction.reactionUser;
  if (!user) return null;

  const displayName = formatUserDisplayName(user.name, user.lastname);
  const vendorId = storeUser?.selectedVendor?.id;

  return (
    <div className="flex items-center gap-2.5 rounded-lg px-1 py-2 sm:px-0.5">
      <div className="relative shrink-0">
        <UserAvatar user={user} size={32} />
        <span
          className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-white text-[11px] leading-none shadow-sm dark:bg-zinc-800"
          aria-hidden
        >
          {emoji}
        </span>
      </div>
      <UserTooltip user={user as Partial<IUser>} side="top">
        <div className="min-w-0 flex-1 text-left leading-tight">
          <a
            href={`/profile/${user.id}?vendorId=${vendorId ?? ""}`}
            target="_blank"
            rel="noreferrer"
            className="block truncate text-[13px] font-medium leading-snug capitalize text-slate-900 dark:text-zinc-50"
          >
            {displayName.toLowerCase()}
          </a>
          {user.email ? (
            <p className="truncate text-[9px] leading-snug text-slate-500 dark:text-zinc-400">
              {user.email}
            </p>
          ) : null}
        </div>
      </UserTooltip>
    </div>
  );
}

const ReactionList = ({
  reactions,
  loading,
  emptyTitle,
  emptyDescription,
}: {
  reactions: IMessageReaction[];
  loading: boolean;
  emptyTitle: string;
  emptyDescription?: string;
}) => {
  const { t } = useTranslation("common");

  if (loading) {
    return (
      <PageLoadingStateV2 embedded title={t("Cargando reacciones")} />
    );
  }

  if (reactions.length === 0) {
    return (
      <PageEmptyStateV2
        embedded
        icon={<span className="text-3xl leading-none">👋</span>}
        title={emptyTitle}
        description={emptyDescription}
      />
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {reactions.map((reaction: IMessageReaction) => (
        <ReactionUserRowV2
          key={`${reaction.reactionUser?.id}-${reaction.emoji}`}
          reaction={reaction}
          emoji={REACTION_EMOJIS[reaction.emoji as ReactionType] ?? "👍"}
        />
      ))}
    </div>
  );
};

const Reactions = ({
  id,
  type,
  embedded = false,
  onLoadingChange,
  onTabChange,
  onCountChange,
}: IReactionPageProps) => {
  const { t } = useTranslation("common");
  const [active, setActive] = useState<string>("all");
  const [reactions, setReactions] = useState<IMessageReaction[]>([]);
  const shellOwnsLoading = Boolean(onLoadingChange);
  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;

  const { loading } = useQuery(gqlMessage.queries.GET_MESSAGE_REACTION, {
    fetchPolicy: "network-only",
    skip: !id,
    variables: {
      message: id,
      type,
    },
    onCompleted: (data) => {
      setReactions(data.getMessageReactions);
    },
  });

  useLayoutEffect(() => {
    onLoadingChangeRef.current?.(loading);
  }, [loading]);

  useEffect(() => {
    return () => onLoadingChangeRef.current?.(false);
  }, []);

  const uniqueReactions = useMemo(() => {
    return (
      reactions?.filter(
        (reaction, index, self) =>
          index ===
          self.findIndex(
            (item) =>
              item.reactionUser?.id === reaction.reactionUser?.id &&
              item.emoji === reaction.emoji
          )
      ) || []
    );
  }, [reactions]);

  useEffect(() => {
    // Avoid flashing "Sin reacciones todavía" while the query is in flight.
    if (loading) return;
    onCountChange?.(uniqueReactions.length);
  }, [loading, uniqueReactions.length, onCountChange]);

  const reactionCounts = useMemo(() => {
    const counts = {
      all: uniqueReactions.length,
      like: 0,
      love: 0,
      wow: 0,
      haha: 0,
      sad: 0,
      angry: 0,
    };

    uniqueReactions.forEach((reaction) => {
      const emoji = reaction.emoji as ReactionType;
      if (emoji in counts) counts[emoji]++;
    });

    return counts;
  }, [uniqueReactions]);

  const filteredReactions = useMemo(() => {
    if (active === "all") return uniqueReactions;
    return uniqueReactions.filter((reaction) => reaction.emoji === active);
  }, [uniqueReactions, active]);

  const tabItems = useMemo((): LiquidPillGroupItem[] => {
    const items: LiquidPillGroupItem[] = [
      {
        value: "all",
        label: (
          <span className="flex items-center gap-1">
            {t("All")}
            {reactionCounts.all > 0 ? (
              <span className="opacity-70">{reactionCounts.all}</span>
            ) : null}
          </span>
        ),
        variant: "slate",
      },
    ];

    Object.entries(REACTION_EMOJIS).forEach(([key, emoji]) => {
      const count = reactionCounts[key as ReactionType];
      if (count > 0) {
        items.push({
          value: key,
          label: (
            <span className="flex items-center gap-1">
              <span className="text-xs">{emoji}</span>
              <span className="opacity-70">{count}</span>
            </span>
          ),
          variant:
            key === "love"
              ? "rose"
              : key === "haha"
                ? "gold"
                : key === "wow"
                  ? "sunset"
                  : key === "sad"
                    ? "sky"
                    : key === "angry"
                      ? "coral"
                      : "ocean",
        });
      }
    });

    if (!loading && onTabChange) {
      onTabChange(items, active, setActive);
    }

    return items;
  }, [reactionCounts, t, active, loading, onTabChange]);

  const emptyTitle =
    active === "all"
      ? t("No reactions yet")
      : t("No hay reacciones en esta categoría");

  // Shell bubbleReveal owns the compact spinner — keep body empty while loading.
  if (loading && shellOwnsLoading) {
    return <div className="w-full bg-transparent" aria-hidden />;
  }

  return (
    <div className={embedded ? "w-full bg-transparent" : "w-full bg-transparent p-4 sm:p-5"}>
      {!loading && !onTabChange ? (
        <div className="mb-4 flex justify-end">
          <LiquidPillGroup
            items={tabItems}
            activeValue={active}
            onChange={setActive}
            size={embedded ? "sm" : "md"}
          />
        </div>
      ) : null}

      <section
        role="region"
        aria-label={`${filteredReactions.length} reactions`}
        className="w-full"
      >
        <ReactionList
          reactions={filteredReactions}
          loading={loading && !shellOwnsLoading}
          emptyTitle={emptyTitle}
          emptyDescription={
            active === "all"
              ? t("Sé el primero en reaccionar")
              : undefined
          }
        />
      </section>
    </div>
  );
};

export default Reactions;
